import { useEffect, useRef, useState } from "react";
import API_BASE from "../config";
import s from "./NotificationBanner.module.css";

/** Slim banner that surfaces "your record was changed by someone else"
 *  notifications inline at the top of a page. Fetches from
 *  /api/notifications, filters to unread rows whose `entityType`
 *  matches the page's interest (TIMESHEET on MyTimesheet,
 *  CALENDAR_EVENT on MyCalendar), and lets the user dismiss each row
 *  individually — dismiss = mark-read on the server.
 *
 *  Re-fetches on mount, every `pollMs`, and whenever the cross-page
 *  "approvals:changed" event fires (so an action elsewhere lands here
 *  without waiting on the poll). */
export default function NotificationBanner({ entityTypes, pollMs = 60000, max = 3 }) {
  const [items, setItems] = useState([]);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    let timer;

    async function load() {
      try {
        const res = await fetch(`${API_BASE}/api/notifications?limit=20`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled.current || !Array.isArray(data)) return;
        const wanted = new Set(entityTypes || []);
        const filtered = data
          .filter(n => !n.readAt && wanted.has(n.entityType))
          .slice(0, max);
        setItems(filtered);
      } catch { /* network blip — try again next poll */ }
    }

    load();
    timer = setInterval(load, pollMs);
    const onChange = () => load();
    window.addEventListener("approvals:changed", onChange);
    return () => {
      cancelled.current = true;
      clearInterval(timer);
      window.removeEventListener("approvals:changed", onChange);
    };
    // entityTypes is stable enough in practice — the parent passes a
    // literal array; but JSON-stringifying it for the dep array would
    // be safer. Kept simple to avoid the dep-array dance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function dismiss(n) {
    // Optimistic remove — flip the UI before the network call so the
    // animation feels snappy. If the server rejects, the next poll
    // will refresh the list and the row reappears.
    setItems(prev => prev.filter(x => x.id !== n.id));
    try {
      await fetch(`${API_BASE}/api/notifications/${n.id}/read`, { method: "POST" });
    } catch { /* will reconcile on next poll */ }
  }

  if (items.length === 0) return null;

  return (
    <div className={s.stack} role="status" aria-live="polite">
      {items.map((n, i) => {
        const meta = parseMetadata(n.metadata);
        return (
          <div key={n.id}
               className={`${s.row} ${typeClass(n.type, s)}`}
               style={{ animationDelay: `${i * 70}ms` }}>
            <span className={s.icon} aria-hidden="true">{typeIcon(n.type)}</span>

            <div className={s.body}>
              <div className={s.message}>{n.message}</div>
              <div className={s.meta}>
                <span className={s.time}>{relTime(n.createdAt)}</span>
                {meta.note && (
                  <span className={s.note}>— "{meta.note}"</span>
                )}
              </div>
            </div>

            <button type="button"
                    className={s.close}
                    onClick={() => dismiss(n)}
                    aria-label="Dismiss notification"
                    title="Got it">×</button>
          </div>
        );
      })}
    </div>
  );
}

function typeClass(type, s) {
  switch (type) {
    case "LEAVE_APPROVED": return s.rowApproved;
    case "LEAVE_REJECTED": return s.rowRejected;
    default:               return s.rowEdited;
  }
}

/** Tiny inline SVG per type. Kept as JSX so colour inherits from
 *  currentColor and we don't need an extra svg file. */
function typeIcon(type) {
  if (type === "LEAVE_APPROVED") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12l5 5L20 7" />
      </svg>
    );
  }
  if (type === "LEAVE_REJECTED") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    );
  }
  return (
    /* edit / pencil */
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function parseMetadata(raw) {
  if (!raw) return {};
  try { return JSON.parse(raw) || {}; }
  catch { return {}; }
}

function relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000)        return "just now";
  if (ms < 3_600_000)     return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000)    return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}
